/**
 * 出图形态层 —— **一次出几张、每张是什么**，以及档位决定的规格真源。
 *
 * ===========================================================================
 * 为什么从 `DetailPageStudio.tsx` 提到这里（2026-09-29）
 * ===========================================================================
 *
 * 「主图 · 套图 · 详情」这一条线上有两件与**页面无关**的事：
 *   1. 形态 → 默认张数与比例（`defaultsFor`）
 *   2. 档位 → 生效的详情规格（`specFor`）
 * 加上主图的逐张解构（`MAIN_SHOTS`）、张数上限（`MAX_BATCH_COUNT`）
 * 与比例/尺寸候选（`sizeChoicesFor` / `ratioChoicesFor`）。
 *
 * 它们之前长在页面文件里，于是**批量工作台**（`pages/BatchStudio.tsx`）一旦要用，
 * 只有两条路：复制一份（同一件事两处维护，改一处漏一处是注定的），
 * 或者把整页引进来。所以上提 —— 一份实现，两个页面引用。
 *
 * ⚠️ 这里只有「形态」这一层。**产品信息 → 提示词**是另一层，在
 * `lib/detailPrompts.ts`。两层分开是因为改动的理由不同：
 * 形态层跟着甲方规格变，提示词层跟着模型表现变。
 */
import type { PlatformProfile } from '@/config/platforms'
import {
  MODULE_MAX_TIMES,
  displaySize,
  moduleShotCount,
  moduleSlots,
  sizeLabel,
  slotCount,
  specSlots,
  type DetailModule,
  type DetailSpec,
} from '@/config/platformDetails'
import { planDetailShots, type PlannedShot } from '@/config/detailPlan'
import type { StudioModeId } from '@/config/studioModes'

/** 一张图的完整定义：出多大、叫什么、这一屏替买家解决什么 */
export type Shot = {
  name: string
  /** 生成尺寸：平台官方成品像素（`970x600`）或比例（`1:1`） */
  size: string
  task: string
  /** 画面内不得出现文字（Amazon 主图这类合规硬约束） */
  textFree?: boolean
  /**
   * 这张是**移动端专属资产**（不是桌面图裁切）。
   * Amazon 高级 A+ 的全宽模块要求分别上传桌面与移动两份文件，
   * 桌面 1464×600（2.44:1）与移动 600×450（4:3）比例差得远。
   */
  isMobile?: boolean
}

/**
 * 张数上限 —— **上限不等于官方图位数**（2026-09-30 用户口径）。
 *
 * 原话：「我是说可以我们自己输入数量！！」。所以张数控件从「一串固定按钮」
 * 变成**自由输入**，`1..99` 只做一个防误输的闸（99 张 ≈ ¥10–30）。
 *
 * 超过官方图位数**不该被拦**：`planDetailShots` 会循环官方模块序列并
 * 标出「第 N 轮」（TikTok Shop 8 个图位要 12 张 = 官方序列走 1.5 轮）。
 * 上一版 `countChoicesFor` 把上限锁死在图位数上，用户想多出几张是**没有入口**的。
 *
 * ⚠️ 声明位置在文件靠前处（2026-10-07 从下面挪上来）：主图配比的
 * `normalizeMix` / `bumpMix` 也读它，虽然函数体是运行期才求值、写在后面也不会
 * 报错，但「谁能读我」这件事不该靠调用时机成立。
 */
export const MAX_BATCH_COUNT = 99

/**
 * 主图套图的逐张解构。
 *
 * ⚠️ 这张清单**只能出现在单张指令里**，绝不能混进共用头部。
 * 一次要 5 张时后端会提交 5 个独立子任务，每个子任务上游是独立一次生图；
 * 5 次都喂同一份「1 白底正视 2 侧面 3 场景 4 卖点 5 细节」的话，
 * 模型会理解成「把 5 种结构画进同一张画面」——
 * 于是 5 张结果全是同样的多视角拼贴，而不是 5 张单图。
 *
 * 4 张的构成写死在需求里（公司清单「电商主图（一次 4 张）」）：
 * 白底（正 + 侧两张合一）→ 场景 → 卖点 → 细节。
 * 少一张凑不齐上架要求，多一张超出交付口径。
 * 这里**不带尺寸** —— 尺寸由平台的 `sizes.main` 决定，写进来就又是一份副本。
 */
export const MAIN_SHOTS: Shot[] = [
  {
    name: '白底双角度',
    size: '',
    task: '纯白背景，左图产品正视、右图向右转 45° 侧视，两张拼一张，展示轮廓与厚度',
  },
  { name: '场景使用图', size: '', task: '真实使用场景，产品处于自然使用状态，环境有生活气息' },
  { name: '卖点图示', size: '', task: '产品占画面主体，一侧留出干净的文字安全区（画面本身不要生成文字）' },
  { name: '细节特写', size: '', task: '材质、工艺、缝线或关键结构的近距离特写' },
]

/**
 * 档位 —— 目前只有 Amazon 分档（基本 A+ / 高级 A+）。
 *
 * 这不是「样式变体」，是**规格真源换了一套**：模块数上限（5 vs 7）、
 * 全宽尺寸（970×600 vs 1464×600）、有没有移动端专属资产，全都不同，
 * 而且两档比例不一样（1.62:1 vs 2.44:1）**不能互缩**。
 * 所以它是根决策；只在「平台详情模块」模式下才有意义，别的时候不渲染。
 */
export type Tier = 'basic' | 'premium'

/** 该平台在该档位下真正生效的详情规格（没有升级档的平台永远是基本档） */
export function specFor(p: PlatformProfile, tier: Tier): DetailSpec {
  return tier === 'premium' && p.detailPremium ? p.detailPremium : p.detail
}

/**
 * 模式 → 该模式在这个平台的默认张数与比例。
 *
 * **唯一真源**：甲方的平台规格（`platform.detail` / `platform.sizes`）说了算。
 * 上一版把「4 张 / 6 张 / 6 张 16:9」写死在模式表里，平台换了也不会变，
 * 于是「选 Ozon 出的是淘宝比例」这类错版一路静默到底。
 *
 * `aplus` 的张数 = 该档位的**图位数**（不是模块数）：三图模块要 3 张、
 * 单图+侧栏要 2 张、对比表 1 张。勾上移动端资产再加全宽类的移动图。
 *
 * 派生两个模式的张数由**目标数量**决定，不由用户选（选 4 个平台就是 4 张），
 * 所以这里给的是兜底值 1，真实张数在页面里按勾选实时算。
 */
export function defaultsFor(
  mode: StudioModeId,
  p: PlatformProfile,
  opts?: { detail?: DetailSpec; withMobile?: boolean },
): { outputs: number; ratio: string } {
  const spec = opts?.detail ?? p.detail
  switch (mode) {
    case 'main':
      return { outputs: MAIN_SHOTS.length, ratio: p.sizes.main }
    /*
     * 「详情页长图」（原 `case 'long'`）于 2026-09-30 下架
     * （用户原话「详情的长图不搞先」），「商品套图」（原 `case 'set'`）
     * 同日下架（用户原话「这套图跟详情页重复了」）。
     * ⚠️ 这里只剩三档是**刻意的**：`StudioModeId` 已不含 `long` / `set`，
     * 加回来时必须连这个 `case`、`shapeOf` 的判断、`ratioChoicesFor` 的
     * 竖版那一支一起加 —— 漏掉任何一处，要么画不出逐屏结构、
     * 要么按错误的比例出图（3:4 竖版被出成 1:1）。
     * 这几处都属于「下架时按剩下几档写死」的地方，不是可以只改一处的东西。
     */
    case 'aplus':
      return { outputs: slotCount(spec, opts?.withMobile ?? false), ratio: specSlots(spec)[0]?.size ?? '1:1' }
    case 'copy':
      return { outputs: 1, ratio: '16:9' }
  }
}


/* =========================================================================
 * 形态 → 逐张解构 / 张数 / 选择器选项
 * =========================================================================
 *
 * 这四件都是**从形态派生出来的**，不是用户输入。上提的理由与上面同一件事：
 * 详情页与批量工作台必须算出同一份 —— 各算一遍的话，会出现
 * 「详情页说这批 4 张、批量页说 6 张」而两边都能提交，报价还各按各的算。
 */

/* ------------------------------------------------------------------------ */
/* 主图配比 —— 「每类各出几张」（2026-10-07）                                 */
/* ------------------------------------------------------------------------ */

/**
 * 主图配比的**唯一真源**：与 `MAIN_SHOTS` 逐项对齐的每类张数。
 *
 * 2026-10-07 用户口径：参考竞品那块「自定义配置」（白底图 1 / 场景图 3 /
 * 卖点图 3 / 其他 0，至少 7 张），选了「补上，可自由配比」。
 *
 * ⚠️ **配比就是主图档的张数**，不是张数之外的第二维。上一版主图只有「输出张数」
 * 一个数：想要「场景 3 张」是做不到的（输入 5 只是把白底重来一遍）。
 * 所以这一档**不再渲染张数输入框** —— 两个入口写同一个值，迟早对不上。
 *
 * 0 是合法值（这一类不出图），但**总和至少 1 张**（见 `normalizeMix`）。
 */
export type MainMix = number[]

/** 默认配比：四类各一张 —— 就是加配比之前那套固定四张的构成 */
export function defaultMainMix(): MainMix {
  return MAIN_SHOTS.map(() => 1)
}

/** 配比一共多少张。整批张数、报价、提交的 `outputs` 全部取它 */
export function mixTotal(mix: readonly number[]): number {
  return MAIN_SHOTS.reduce((n, _s, i) => n + Math.max(0, Math.floor(mix[i] ?? 0)), 0)
}

/**
 * 归一化外部来的配比（草稿恢复 / 换形态）。
 *
 * 四件事一起做，缺任何一件都会静默出错：
 *   · **对齐长度** —— 草稿里可能存着更短/更长的数组（`MAIN_SHOTS` 增删过），
 *     `mix[i] ?? 0` 之外的项要丢掉，否则 `mixTotal` 与渲染的行数对不上；
 *   · **逐项夹到 0..MAX_BATCH_COUNT**；
 *   · **总和不超过 `MAX_BATCH_COUNT`** —— 逐项合格不等于总和合格：
 *     手改过的草稿可以是 `[99,99,99,99]`，那就是 396 张，
 *     报价按 396 张算，而界面上只有四个两位数。超出部分**从末类往前扣**；
 *   · **总和至少 1** —— 全是 0 的话 `mainShotsFor` 返回空数组，
 *     报告价 0 元、结构卡空白，看起来像页面坏了。
 */
export function normalizeMix(raw: unknown): MainMix {
  const src = Array.isArray(raw) ? raw : []
  const out = MAIN_SHOTS.map((_s, i) => {
    const n = Math.floor(Number(src[i]))
    return Number.isFinite(n) ? Math.min(MAX_BATCH_COUNT, Math.max(0, n)) : 1
  })
  let over = mixTotal(out) - MAX_BATCH_COUNT
  for (let i = out.length - 1; i >= 0 && over > 0; i--) {
    const cut = Math.min(out[i], over)
    out[i] -= cut
    over -= cut
  }
  if (mixTotal(out) < 1) out[0] = 1
  return out
}

/**
 * 某一类 ±1 —— 界面上的 stepper 只走这一个入口。
 *
 * 两条边界都在这里挡，而且**调用方要把按钮同时置灰**（见 `ShotMixPicker`）：
 *   · **不加到 0 张**：点减号把最后一类减没了，整批就变成 0 张，
 *     而用户看到的只是「数字变成 0」，不会意识到生成按钮已经无图可出；
 *   · **总和不超过 `MAX_BATCH_COUNT`**：四个数字各自看都合理，
 *     加起来 396 张就不是了 —— 报价照着它算，用户却只在界面上看到四个两位数。
 *
 * 被拒绝时**返回原值**（按不动），比报错或静默清空都直接。
 */
export function bumpMix(mix: readonly number[], index: number, delta: number): MainMix {
  const next = normalizeMix(mix)
  if (index < 0 || index >= next.length) return next
  const nextTotal = mixTotal(next) + delta
  if (nextTotal < 1 || nextTotal > MAX_BATCH_COUNT) return next
  next[index] = Math.min(MAX_BATCH_COUNT, Math.max(0, next[index] + delta))
  return next
}

/**
 * 主图模式的逐张解构 —— **不走详情页规划层**。
 *
 * 主图是列表首屏的素材（白底双角度 / 场景 / 卖点 / 细节），
 * 与详情页的买家叙事是两回事，所以仍是固定的四类。
 *
 * 按**类型分组**展开（同类连续），不是四类轮流：竞品那套「白底 1 / 场景 3 /
 * 卖点 3」逐项列出来就是分组的，而逐张清单里「场景使用图 · 第 2 张」挨着
 * 「场景使用图 · 第 3 张」也比四类交替好读。
 *
 * 同一类多张时**必须带「第 N 张」后缀与换机位要求**：不带的话逐张清单里
 * 会出现两行一模一样的名字（像 bug），提示词那边也会让模型画两张相同的图。
 */
export function mainShotsFor(mix: readonly number[], ratio: string): Shot[] {
  if (!MAIN_SHOTS.length) return []
  const counts = normalizeMix(mix)
  const out: Shot[] = []
  MAIN_SHOTS.forEach((base, ti) => {
    const n = counts[ti]
    for (let k = 0; k < n; k++) {
      out.push({
        name: n > 1 ? `${base.name} · 第 ${k + 1} 张` : base.name,
        size: base.size || ratio,
        task:
          k === 0
            ? base.task
            : `${base.task}（同类第 ${k + 1} 张：换机位、换角度或换场景，务必与前面同名的张不重复）`,
        textFree: base.textFree,
      })
    }
  })
  return out
}

/**
 * 本批逐张解构 —— 两条路都在这里，页面不再自己判断走哪条。
 *
 * 返回**两个**：
 *   `shots`        —— 主图的固定解构 / 详情页的逐屏规划（喂给逐张指令）
 *   `plannedShots` —— 只有详情页那条路非空（`promptCtx` 用它判断走哪支）
 * 一次返回两个是为了让「界面列的」与「提示词里写的」必然是同一份 ——
 * 分两次调用就会出现两处各自 plan 一遍。
 */
export function shapeOf(args: {
  mode: StudioModeId
  outputs: number
  ratio: string
  detail: DetailSpec
  withMobile?: boolean
  /**
   * **整批统一尺寸**（用户 2026-09-30 口径：「这个尺寸有默认，但是可以选择」）。
   *
   * 空字符串 = 官方尺寸（逐张按模块规格，默认值）；非空 = 本批每一张都用它。
   * 它的意义是把「官方模块尺寸」从**只读展示**变成**有默认值的可选项** ——
   * 之前那一栏是纯展示（`size-list` 里的 badge 点不动），
   * 用户想换尺寸只能换平台。
   */
  batchSize?: string
  /**
   * **主图配比** —— 只有 `mode === 'main'` 读它（白底 / 场景 / 卖点 / 细节各几张）。
   *
   * ⚠️ 主图这一档**张数就是配比之和**，`outputs` 不再参与：
   * 两个入口写同一个值迟早对不上（用户改了一个、另一个还亮着旧值）。
   * 调用方传 `mix` 就必须把张数输入框收起来 —— 见 `/detail-page` 与 `/batch`。
   */
  mix?: MainMix
  /**
   * **勾选的模块名**（只有 `aplus` 读它）。空数组 = 全部模块。
   *
   * 2026-10-07 用户口径：参考竞品「包含模块 0/16」的逐个勾选，选了「加 16 个
   * 模块逐个勾选，替换掉现在的三档预设」。⚠️ 我们的模块数**不是固定 16** ——
   * 它跟着平台规格走（Amazon 基本档 5、淘宝 8、TikTok Shop 8…），
   * 勾选列表渲染的就是当前平台的官方模块，不许在页面里写死一份 16 项的清单。
   */
  modules?: string[]
  /**
   * **每个模块出几份**（模块名 → 份数；0 = 不出）。传了它，`outputs` 就被忽略 ——
   * 详情档的张数**就是各模块份数之和**（同主图档「配比之和就是张数」）。
   *
   * 页面与批量工作台传的是同一份完整表（`moduleCountsOf` 归一过）：
   * 一个由用户在模块清单上 − / + 调出来，一个来自表格。
   */
  counts?: Record<string, number>
}): { shots: (Shot | PlannedShot)[]; plannedShots: PlannedShot[] } {
  const { mode, outputs, ratio, detail, withMobile, batchSize, mix, modules, counts } = args
  // 模仿版式没有「逐张解构」：它的结构来自用户上传的参考版式图。
  // 给它凭空造一份清单就是第二份真源。
  // ⚠️ 详情页这支**只剩「平台详情模块」一档** —— 「详情页长图」2026-09-30 下架，
  // 但逐屏规划能力本身保留（aplus 仍在用 `planDetailShots`），没有跟着删。
  if (mode !== 'aplus' && mode !== 'main') {
    return { shots: [], plannedShots: [] }
  }
  if (mode === 'main') return { shots: mainShotsFor(mix ?? defaultMainMix(), ratio), plannedShots: [] }
  const planned = planDetailShots(detail, outputs, {
    withMobile: mode === 'aplus' && withMobile,
    modules,
    counts,
  })
  /*
   * 覆盖生效在**规划之后**，而且只覆盖桌面图位：
   * 移动端资产的尺寸是官方硬要求（与桌面比例差得远，见 `MOBILE_TASK`），
   * 把它一起改成桌面尺寸就等于交两份同尺寸文件 —— 那是会被后台拒的。
   * 所以 `isMobile` 的那几张保留规划层算出来的 `mobileSize`。
   */
  const plannedShots = batchSize
    ? planned.map((s) => (s.isMobile ? s : { ...s, size: batchSize }))
    : planned
  return { shots: plannedShots, plannedShots }
}

/**
 * 本批张数。两种口径必须分清，混在一起就会「报价按 6 张算、实际出了 4 张」：
 *   出图（主图 / 平台模块）→ `shots` 展开后的长度；
 *   模仿版式              → 用户选的 `outputs`。
 */
export function countOf(mode: StudioModeId, outputs: number, shots: (Shot | PlannedShot)[]): number {
  const userPicked = mode === 'copy'
  return userPicked ? Math.max(1, outputs) : shots.length || outputs
}

/**
 * 比例选择器的选项 —— 非「平台详情模块」档的那一支（`form.ratio`）。
 *
 * 上一版是硬编码六项 `['1:1','3:4','4:5','16:9','9:16','2:3']` ——
 * 那既不是平台标准、也不是模型能力，是**第三份真源**。
 * 用户报的「比例不够多」在这一档同样成立。
 * 现在：平台自己的标准比例排第一段，其余从**当前模型能出的比例**（后端能力）补，
 * 与 `sizeChoicesFor` 同一套分组、同一个渲染控件（两处长得不一样 = 又一份实现）。
 *
 * ⚠️ 竖版那一支（原 `mode === 'long'` 的 `['3:4', …]`）随「详情页长图」
 * 于 2026-09-30 一起下架。恢复长图时要把那一支加回来，
 * 否则长图会拿到一份以 `1:1` 打头的候选表。
 */
export function ratioChoicesFor(
  mode: StudioModeId,
  profile: PlatformProfile,
  ratios?: readonly string[],
): ChoiceGroup[] {
  const seen = new Set<string>()
  const groups: ChoiceGroup[] = []
  const recommended = collect(
    [defaultsFor(mode, profile).ratio, profile.sizes.main, profile.sizes.secondary],
    seen,
  )
  if (recommended.length) {
    groups.push({ id: 'platform', title: `${profile.name}常用`, options: recommended })
  }
  const others = collect(usableRatios(ratios), seen)
  if (others.length) groups.push({ id: 'more', title: '其他比例', options: others })
  return groups
}

/** 该档位的官方图位数 —— 只用于提示文案（「超过 N 张开始循环」），不再是上限 */
export function slotLimitFor(detail: DetailSpec, withMobile: boolean): number {
  return slotCount(detail, withMobile)
}

/* ------------------------------------------------------------------------ */
/* 包含模块 · 逐模块勾选（2026-10-07）                                        */
/* ------------------------------------------------------------------------ */

/**
 * 勾选生效后的模块清单 —— 空数组 = **全部模块**。
 *
 * 为什么用「空 = 全选」而不是「默认把全部模块名塞进 state」：
 * 默认值必须能跨平台成立，而模块名是**跟着平台规格变的**（Amazon 5 / 淘宝 8 /
 * TikTok Shop 8…）。写一份具体名字当默认值，换平台那一刻它就全成了不存在的模块，
 * 而 `planDetailShots` 过滤后为空 → 用户会看到「已勾选的全都没了、一张都不出」。
 * 空数组天然表示「不限」，换平台/换档位不需要任何迁移。
 *
 * 勾成空（用户把最后一个也取消）= 回到全选。这不是"拦住了用户"，
 * 而是 `[]` 这个值的语义就是全选 —— 归一化到唯一的表征，避免
 * 「空数组」与「全选清单」两份表示同一个意思（改了一处另一处不知道）。
 */
export function normalizeModules(raw: unknown, spec: DetailSpec): string[] {
  const names = spec.modules.map((m) => m.name)
  const picked = Array.isArray(raw) ? names.filter((n) => raw.includes(n)) : []
  // 交集为空有两种情况，都回落到「全选」：用户本来就没勾（`raw` 为空数组），
  // 或者他勾的那些模块都不在当前平台上（换平台/换档位之后）。
  return picked.length === names.length ? [] : picked
}

/**
 * 「每个模块出几份」—— **完整**表（模块名 → 份数）。
 *
 * state 里存的是**完整表**，不是稀疏覆盖：份数 0 与「还没调过」必须能区分开
 * （前者是「这个模块不出」，后者是「按默认 1 份出」）。稀疏表要靠「缺省 = 1」
 * 表达后者，而用户一旦把它调成 0，稀疏表里就会出现一条 0 —— 两个语义挤进
 * 同一个表示，改成 `undefined` 还是 `0` 就全凭调用方记性。
 *
 * 遍历的是 `spec.modules`，所以**换平台之后失效的键名自动被丢掉**，
 * 不需要像旧版那样单独写一次 `normalizeModules` 迁移；反过来，
 * 换回来时旧键名还在（用户上次的选择没丢）。
 */
export function moduleCountsOf(spec: DetailSpec, raw: unknown): Record<string, number> {
  const table = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const out: Record<string, number> = {}
  for (const m of spec.modules) {
    const v = Math.floor(Number(table[m.name]))
    out[m.name] = Number.isFinite(v) ? Math.min(MODULE_MAX_TIMES, Math.max(0, v)) : 1
  }
  return out
}

/** 份数 > 0 的模块名（保持官方顺序）—— 提示词层与 `planDetailShots` 要的是它 */
export function modulesFromCounts(spec: DetailSpec, counts: Record<string, number>): string[] {
  return spec.modules.filter((m) => (counts[m.name] ?? 0) > 0).map((m) => m.name)
}

/** 这一批总共几张 —— 份数 × 每份张数之和（界面文案与核对用；报价走 `countOf`） */
export function moduleShotsTotal(
  spec: DetailSpec,
  withMobile: boolean,
  counts: Record<string, number>,
): number {
  return spec.modules.reduce(
    (n, m) => n + (counts[m.name] ?? 0) * moduleShotCount(m, withMobile),
    0,
  )
}

/** 勾选生效后还剩几个模块 —— 界面上的「已选 N/M」取它，不另算一份 */
export function pickedModuleCount(modules: readonly string[] | undefined, spec: DetailSpec): number {
  const kept = normalizeModules(modules, spec)
  return kept.length || spec.modules.length
}

/**
 * 勾选模块的**图位总数** —— 勾选变化时写进 `form.outputs` 的值。
 *
 * ⚠️ 单位是「张」不是「模块个数」：官方多图模块（A+ 三图 / 单图+侧栏）
 * 一个模块顶 3 张、2 张，`withMobile` 打开后全宽类模块还要各加一张。
 * 把模块个数当张数写进 `outputs`，用户勾了 3 个模块只会出 1–2 张，
 * 而且**不报错** —— 只在逐张清单里少几行。
 *
 * 空勾选（= 全选）时返回的就是 `slotCount(spec, withMobile)`，与「全部图位」一致。
 */
export function moduleSlotCount(
  spec: DetailSpec,
  withMobile: boolean,
  modules: readonly string[] | undefined,
): number {
  const kept = normalizeModules(modules, spec)
  const use = kept.length ? spec.modules.filter((m) => kept.includes(m.name)) : spec.modules
  return use.reduce((n, m) => {
    const slots = moduleSlots(m)
    return n + slots.length + (withMobile ? slots.filter((s) => s.mobileSize).length : 0)
  }, 0)
}

/** 把用户敲进输入框的值收敛到 `1..MAX_BATCH_COUNT`（空串 / NaN 回落到 1） */
export function clampCount(raw: unknown): number {
  const n = Math.floor(Number(raw))
  if (!Number.isFinite(n)) return 1
  return Math.min(MAX_BATCH_COUNT, Math.max(1, n))
}

/* ------------------------------------------------------------------------ */
/* 比例 / 尺寸候选 —— 一份真源，详情页与批量页共用                             */
/* ------------------------------------------------------------------------ */

/** 下拉里的一个选项 */
export type ChoiceOption = { value: string; label: string }

/**
 * 一段分组 —— 与原生下拉的 `<optgroup>` 一一对应。
 *
 * 为什么要分组：候选从 4 项涨到十几个（当前模型的能力里 duomi 有 13 个比例），
 * 平铺一行按钮会换行占掉半屏；而且「官方尺寸 / 平台常用 / 其他比例」
 * 这三种**语义不同**的东西混在一起，用户分不出「哪个是官方要求」。
 */
export type ChoiceGroup = { id: string; title: string; options: ChoiceOption[] }

/** 摊平分组 —— 给「这个值还在不在候选表里」这类判断用（渲染用分组本身） */
export function flatChoices(groups: ChoiceGroup[]): ChoiceOption[] {
  return groups.flatMap((g) => g.options)
}

/**
 * 从模型能力里挑出**真能当画面尺寸用**的比例。
 *
 * 后端 `capabilities.aspect_ratios` 是这份清单的唯一真源
 * （图片档：duomi 直连 13 个 / 网关 6 个）。但那份清单里混着非比例的值 ——
 * 视频模型的 `adaptive` 是「跟着参考素材走」，拿来当尺寸就是一个选了没用的选项。
 *
 * 返回空数组 = **后端没说**（旧网关模型的 `capabilities` 是空对象，
 * 见 `api/types.ts` 的两条铁律）。调用方据此退化，**不凭空虚造**一份通版比例表。
 */
function usableRatios(ratios?: readonly string[]): string[] {
  if (!ratios?.length) return []
  return ratios.filter((r) => typeof r === 'string' && /^\d+(?:\.\d+)?:\d+(?:\.\d+)?$/.test(r.trim()))
}

/** 按序去重收集 —— 两份清单（平台档案 / 模型能力）里的重复项很多（1:1、3:4 到处都是） */
function collect(values: (string | undefined)[], seen: Set<string>): ChoiceOption[] {
  const out: ChoiceOption[] = []
  for (const v of values) {
    const r = (v ?? '').trim()
    if (!r || seen.has(r)) continue
    seen.add(r)
    out.push({ value: r, label: sizeLabel(r) })
  }
  return out
}

/**
 * 输出尺寸的候选项 —— **整批一个尺寸**（2026-09-30 用户口径）。
 *
 * 三段，全部来自**已有的真源**，这里不新造清单：
 *   · `官方尺寸`     —— `''` = 逐张按模块规格，**默认值**；
 *   · `<平台>常用`   —— `PlatformProfile.sizes` 那三档（主图 / 副图 / 视频）；
 *   · `其他比例`     —— 当前模型 `capabilities.aspect_ratios` 里剩下的全部。
 *
 * 第三段是 2026-09-30 第二轮补的。用户原话：「比例不够多」——
 * 上一版候选只从平台档案的 3 档派生（TikTok Shop 就只剩 1:1 / 3:4 / 9:16），
 * 而模型真正能出的远不止这些。那份全量清单**只从后端拿**，前端写第二份必然分叉
 * （本项目 09-29 那个「选了 4:5 到手方图还照样扣费」就是两份清单分叉的后果）。
 *
 * 为什么给比例而不是给像素：像素真源在后端 `image_sizes.DUOMI_IMAGE_SIZES`，
 * 而且后端本来就收比例；前端再写一份像素清单等于把换算抄第二遍。
 *
 * 「官方尺寸」那一项的文案带上实际值（单尺寸档位写死数字，多尺寸档位写「逐张匹配」）：
 * 只说「官方尺寸」的话，用户不知道切回去会变成什么。
 */
export function sizeChoicesFor(
  detail: DetailSpec,
  profile: PlatformProfile,
  ratios?: readonly string[],
): ChoiceGroup[] {
  const official = [...new Set(specSlots(detail).map((s) => s.size))]
  const officialLabel =
    official.length === 1 ? `官方尺寸 ${displaySize(official[0])}` : '官方尺寸（逐张匹配）'

  // `''` 先占位：平台档案里若恰好也写了一个空比例，不许挤掉官方尺寸那一项
  const seen = new Set<string>([''])
  const groups: ChoiceGroup[] = [
    { id: 'official', title: '官方尺寸', options: [{ value: '', label: officialLabel }] },
  ]
  const recommended = collect(
    [profile.sizes.main, profile.sizes.secondary, profile.sizes.video],
    seen,
  )
  if (recommended.length) {
    groups.push({ id: 'platform', title: `${profile.name}常用`, options: recommended })
  }
  const others = collect(usableRatios(ratios), seen)
  if (others.length) groups.push({ id: 'more', title: '其他比例', options: others })
  return groups
}
